/* eslint-disable react-refresh/only-export-components */
import { useContext, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import { FeatureFlagKey, useFeatureFlags, useLaunchDarklyReady } from '../hooks/useFeatureFlags';
import { useMountEffect } from '../hooks/useMountEffect';
import { useAuth } from '../hooks/useAuth';
import { detectPspHostingEnvironment } from '../services/utils';
import { HostingEnvironment, type AppRegistryCallbackContext } from '@opengov/psp-contract';
import {
  AppConfig,
  UnresolvedAppConfig,
  RouteRegistryEntry,
  ResolvedRouteRegistryEntry,
  PspAppContext,
  type Logger,
} from '../types';
import { auth0Config } from '../utils';
import { useEntity } from './EntityContext';
import { appRegistry as unresolvedAppRegistry } from '../services/appRegistry';
import { routeRegistry as unresolvedRouteRegistry } from '../services/routeRegistry';
import { applyDevToolOverrides } from '../services/appRegistryDevTools';
import { buildGabDynamicAppConfigs, type GabDynamicApps } from '../services/gabDynamicAppConfigs';
import { useLoggerWithContext } from '../services/logger/loggerContext';
import { moduleUrlFor, setSessionEndModuleUrlResolver } from '../services/sessionEndApps';
import { PspContext } from './PspContextDefinition';

/**
 * Detects path collisions between the app registry and route registry.
 * App registry takes precedence when collisions occur.
 * @internal Exported for testing
 */
export function detectPathCollisions(
  appRegistry: AppConfig[],
  routeRegistry: ResolvedRouteRegistryEntry[]
): void {
  const appPaths = new Set(appRegistry.map((app) => app.basePath));
  const collisions = routeRegistry
    .filter((route) => appPaths.has(route.basePath))
    .map((route) => route.basePath);

  if (collisions.length > 0) {
    const message = `[PspContext] Path collision between registries: ${collisions.join(', ')}. App registry takes precedence.`;
    if (import.meta.env.DEV) {
      console.warn(message);
    } else {
      console.error(message);
    }
  }
}

/** Logs each gab-core app entitlement that yielded no entry, so a missing item can be traced. */
function logSkippedGabApps(logger: Logger, skipped: GabDynamicApps['skipped']): void {
  for (const { entitlementKey, reason } of skipped) {
    logger.warn('gab-core app entitlement has no sidebar entry', { entitlementKey, reason });
  }
}

interface PspContextProviderProps {
  children: ReactNode;
  allowDevToolOverrides?: boolean;
}

export function PspContextProvider({
  children,
  allowDevToolOverrides = false,
}: PspContextProviderProps) {
  const [pspContext, setPspContext] = useState<PspAppContext | undefined>(undefined);
  const { user, token, getAccessTokenSilently } = useAuth();
  const { activeEntity, activeEntityEntitledApplications } = useEntity();
  // GAB app entitlements are created at runtime, so their entries are built from the
  // user's entitlements in the active entity rather than listed in the static registry. The build
  // is pure and depends on the entitlements; the resolution effect logs its skips.
  const gabDynamicApps = useMemo(
    () => buildGabDynamicAppConfigs(unresolvedAppRegistry, activeEntityEntitledApplications),
    [activeEntityEntitledApplications]
  );
  // Read through a ref: the base logger gets a new identity when Faro initializes and when the
  // browser goes offline or back online, and that must not re-run registry resolution.
  const logger = useLoggerWithContext();
  const loggerRef = useRef(logger);
  useEffect(() => {
    loggerRef.current = logger;
  }, [logger]);
  // The skip list last logged, so a re-run of the resolution effect (new flags or token, or
  // StrictMode's replay in development) doesn't log the same list again.
  const loggedGabAppSkips = useRef<GabDynamicApps['skipped'] | null>(null);
  const featureFlags = useFeatureFlags();
  const ldReady = useLaunchDarklyReady();

  // Cleared only on unmount, not on each re-resolve: a sign-out during a re-resolve still finds
  // the registries from before it rather than none.
  useMountEffect(() => () => setSessionEndModuleUrlResolver(undefined));

  useEffect(() => {
    // Wait for LD identify(); resolving with default-false flags would drop
    // every flag-gated app from the registry.
    if (!ldReady) return;

    if (
      !!featureFlags[FeatureFlagKey.PSP_UI_DYNAMIC_GAB_APPS] &&
      loggedGabAppSkips.current !== gabDynamicApps.skipped
    ) {
      loggedGabAppSkips.current = gabDynamicApps.skipped;
      logSkippedGabApps(loggerRef.current, gabDynamicApps.skipped);
    }

    // Cancellation flag to prevent stale state updates on rapid re-renders or unmounts
    let cancelled = false;

    const hostingEnvironment: HostingEnvironment = detectPspHostingEnvironment();
    // Attach getAccessToken so registry callbacks (and any non-React consumer
    // reading auth0Config off mount args) can fetch fresh tokens via the
    // shell's single Auth0 client without standing up their own SDK.
    const auth0ConfigWithToken = {
      ...auth0Config,
      ...(token ? { token } : {}),
      getAccessToken: getAccessTokenSilently,
    };
    const userInfo = user ? { email: user.email, sub: user.sub, name: user.name } : undefined;
    const callbackContext: AppRegistryCallbackContext = {
      hostingEnvironment,
      activeEntity: activeEntity || undefined,
      auth0Config: auth0ConfigWithToken,
      user: userInfo,
      featureFlags,
    };

    // Filter out entries gated by a LaunchDarkly flag that is currently off
    const flagFilteredRegistry = [...unresolvedAppRegistry, ...gabDynamicApps.configs].filter(
      (app) => !app.featureFlagKey || !!featureFlags[app.featureFlagKey]
    );

    // Route registry entries gated by a LaunchDarkly flag that is off don't exist.
    const routeRegistryEntries = unresolvedRouteRegistry.filter(
      (route) => !route.featureFlagKey || !!featureFlags[route.featureFlagKey]
    );

    // Resolve app registry callbacks (handling both sync and async)
    const appRegistryPromise = Promise.all(
      flagFilteredRegistry.map(async (app: UnresolvedAppConfig) => {
        // Resolve moduleRoutes if present
        const resolvedModuleRoutes =
          'moduleRoutes' in app && app.moduleRoutes
            ? await Promise.all(
                app.moduleRoutes.map(async (route) => {
                  const resolvedUrl = route.resolver(callbackContext);
                  return {
                    pattern: route.pattern,
                    resolver: resolvedUrl instanceof Promise ? await resolvedUrl : resolvedUrl,
                  };
                })
              )
            : undefined;

        // Resolve unauthenticatedRoutes if present (same pattern as moduleRoutes)
        const resolvedUnauthRoutes =
          'unauthenticatedRoutes' in app && app.unauthenticatedRoutes
            ? await Promise.all(
                app.unauthenticatedRoutes.map(async (route) => {
                  const resolvedUrl = route.resolver(callbackContext);
                  return {
                    pattern: route.pattern,
                    resolver: resolvedUrl instanceof Promise ? await resolvedUrl : resolvedUrl,
                  };
                })
              )
            : undefined;

        // Resolve externalUrl function if present (can be sync or async)
        let resolvedExternalUrl: string | undefined = undefined;
        if ('externalUrl' in app) {
          if (typeof app.externalUrl === 'function') {
            const result = app.externalUrl(callbackContext);
            resolvedExternalUrl = result instanceof Promise ? await result : result;
          } else if (typeof app.externalUrl === 'string') {
            resolvedExternalUrl = app.externalUrl;
          }
        }

        const resolvedTriggerRefresh = !!app.triggerPageRefreshOnNavigateAway(callbackContext);

        return {
          ...app,
          moduleRoutes: resolvedModuleRoutes,
          unauthenticatedRoutes: resolvedUnauthRoutes,
          externalUrl: resolvedExternalUrl,
          triggerPageRefreshOnNavigateAway: resolvedTriggerRefresh,
        } as AppConfig;
      })
    );

    // Resolve route registry callbacks (handling both sync and async)
    const routeRegistryPromise = Promise.all(
      routeRegistryEntries.map(async (route: RouteRegistryEntry) => {
        // Resolve moduleRoutes if present (optional - route may have component instead)
        const resolvedModuleRoutes = route.moduleRoutes
          ? await Promise.all(
              route.moduleRoutes.map(async (moduleRoute) => {
                const resolvedUrl = moduleRoute.resolver(callbackContext);
                return {
                  pattern: moduleRoute.pattern,
                  resolver: resolvedUrl instanceof Promise ? await resolvedUrl : resolvedUrl,
                };
              })
            )
          : undefined;

        // Resolve unauthenticatedRoutes if present
        const resolvedUnauthRoutes = route.unauthenticatedRoutes
          ? await Promise.all(
              route.unauthenticatedRoutes.map(async (unauthRoute) => {
                const resolvedUrl = unauthRoute.resolver(callbackContext);
                return {
                  pattern: unauthRoute.pattern,
                  resolver: resolvedUrl instanceof Promise ? await resolvedUrl : resolvedUrl,
                };
              })
            )
          : undefined;

        const triggerRefresh = route.triggerPageRefreshOnNavigateAway;
        const resolvedTriggerRefresh = triggerRefresh
          ? !!triggerRefresh(callbackContext)
          : undefined;

        return {
          id: route.id,
          basePath: route.basePath,
          moduleRoutes: resolvedModuleRoutes,
          unauthenticatedRoutes: resolvedUnauthRoutes,
          moduleConfig: route.moduleConfig,
          description: route.description,
          component: route.component,
          triggerPageRefreshOnNavigateAway: resolvedTriggerRefresh,
        } as ResolvedRouteRegistryEntry;
      })
    );

    // Wait for both registries to resolve, then set context
    Promise.all([appRegistryPromise, routeRegistryPromise]).then(
      ([resolvedAppRegistry, resolvedRouteRegistry]) => {
        // Check cancellation flag before updating state
        if (cancelled) return;

        // Apply dev tool overrides to all resolved app configs only if allowOverrides is true
        // This ensures that any dev tool overrides from sessionStorage are applied
        // before the configs are shared throughout the application via context
        const appRegistry = allowDevToolOverrides
          ? resolvedAppRegistry.map((app) => applyDevToolOverrides(app))
          : resolvedAppRegistry;

        detectPathCollisions(appRegistry, resolvedRouteRegistry);

        // Before dev-tool overrides: sign-out may load a module the user never opened in this
        // page, so a sessionStorage override must not choose its code.
        setSessionEndModuleUrlResolver((module) =>
          moduleUrlFor(module, resolvedAppRegistry, resolvedRouteRegistry)
        );

        setPspContext({
          hostingEnvironment,
          appRegistry,
          routeRegistry: resolvedRouteRegistry,
          activeEntity: activeEntity || undefined,
          auth0Config: auth0ConfigWithToken,
          allowDevToolOverrides: allowDevToolOverrides,
        });
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    user,
    token,
    getAccessTokenSilently,
    activeEntity,
    allowDevToolOverrides,
    featureFlags,
    ldReady,
    gabDynamicApps,
  ]);

  if (!pspContext) {
    return null;
  }

  return <PspContext.Provider value={pspContext}>{children}</PspContext.Provider>;
}

export function usePspContext() {
  const context = useContext(PspContext);
  if (context === undefined) {
    throw new Error('usePspContext must be used within a PspContextProvider');
  }
  return context;
}
